import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from './local-storage.service';

export type ThemeMode = 'light' | 'dark' | 'device';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  private readonly STORAGE_KEY = 'app-theme';
  private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private _mode: BehaviorSubject<ThemeMode>;

  mode$: ReturnType<BehaviorSubject<ThemeMode>['asObservable']>;

  private mediaListener = () => {
    if (this._mode.value === 'device') this.applyTheme('device');
  };

  constructor(private localStorage_$: LocalStorageService) {
    this._mode = new BehaviorSubject<ThemeMode>(this.storedMode());
    this.mode$ = this._mode.asObservable();
    this.mediaQuery.addEventListener('change', this.mediaListener);
    this.applyTheme(this._mode.value);
  }

  get mode(): ThemeMode {
    return this._mode.value;
  }

  setTheme(mode: ThemeMode): void {
    this.localStorage_$.setItem(this.STORAGE_KEY, mode);
    this._mode.next(mode);
    this.applyTheme(mode);
  }

  ngOnDestroy(): void {
    this.mediaQuery.removeEventListener('change', this.mediaListener);
  }

  private storedMode(): ThemeMode {
    const stored = this.localStorage_$.getItem(this.STORAGE_KEY) as ThemeMode;
    return ['light', 'dark', 'device'].includes(stored) ? stored : 'light';
  }

  private applyTheme(mode: ThemeMode): void {
    const isDark = mode === 'dark' || (mode === 'device' && this.mediaQuery.matches);
    document.body.classList.toggle('dark-theme', isDark);
  }
}
